import { ref, get, set, push, serverTimestamp, update, onValue, query, orderByChild } from './phpDataAdapter';
import { database } from '../config/backend';

export interface AdminChatMessage {
  id: string;
  author: 'nutritionist' | 'admin';
  text: string;
  createdAt: string;
  image?: string;
  audio?: string;
  rawTimestamp?: number;
}

export interface AdminChatMetadata {
  nutritionistId: string;
  nutritionistName: string;
  nutritionistEmail: string;
  lastMessage: string;
  lastMessageTime: number;
  unreadCount: number;
  unreadCountNutritionist: number;
}

/**
 * Busca ou cria conversa com admin
 */
export async function getAdminChatConversation(nutritionistId: string): Promise<AdminChatMetadata | null> {
  if (!nutritionistId) return null;

  try {
    // Buscar metadados da conversa
    const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);
    const metadataSnap = await get(metadataRef);

    if (metadataSnap.exists()) {
      return metadataSnap.val() as AdminChatMetadata;
    }

    // Se não existe, buscar dados do nutricionista e criar conversa
    const userRef = ref(database, `users/${nutritionistId}`);
    const userSnap = await get(userRef);

    if (!userSnap.exists()) {
      return null;
    }

    const userData = userSnap.val();
    const metadata: AdminChatMetadata = {
      nutritionistId,
      nutritionistName: userData.name || 'Nutricionista',
      nutritionistEmail: userData.email || '',
      lastMessage: '',
      lastMessageTime: Date.now(),
      unreadCount: 0,
      unreadCountNutritionist: 0,
    };

    await set(metadataRef, metadata);
    return metadata;
  } catch (error) {
    console.error('Erro ao buscar/criar conversa com admin:', error);
    return null;
  }
}

/**
 * Busca mensagens da conversa com admin
 */
export async function getAdminChatMessages(nutritionistId: string): Promise<AdminChatMessage[]> {
  if (!nutritionistId) return [];

  try {
    const messagesRef = ref(database, `adminChat/${nutritionistId}/messages`);
    const messagesSnap = await get(messagesRef);

    if (!messagesSnap.exists()) {
      return [];
    }

    const messagesData = messagesSnap.val() as Record<string, any>;
    const messages: Array<AdminChatMessage & { rawTimestamp: number }> = [];

    Object.entries(messagesData).forEach(([id, data]) => {
      const timestamp = data.createdAt || data.timestamp || '';
      let rawTimestamp = 0;

      if (typeof timestamp === 'object' && timestamp.seconds) {
        rawTimestamp = timestamp.seconds * 1000;
      } else if (typeof timestamp === 'string') {
        rawTimestamp = new Date(timestamp).getTime();
      } else if (typeof timestamp === 'number') {
        rawTimestamp = timestamp;
      }

      messages.push({
        id,
        author: data.author || 'nutritionist',
        text: data.text || '',
        createdAt: formatChatTime(timestamp),
        image: data.image,
        audio: data.audio,
        rawTimestamp,
      });
    });

    // Ordenar por timestamp (mais antigas primeiro)
    messages.sort((a, b) => a.rawTimestamp - b.rawTimestamp);

    return messages.map(({ rawTimestamp, ...msg }) => msg);
  } catch (error) {
    console.error('Erro ao buscar mensagens do admin chat:', error);
    return [];
  }
}

/**
 * Formata timestamp para exibição
 */
function formatChatTime(value: any): string {
  if (!value) return '';

  try {
    let date: Date;

    if (typeof value === 'object' && value.seconds) {
      date = new Date(value.seconds * 1000);
    } else if (typeof value === 'string') {
      date = new Date(value);
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else {
      return '';
    }

    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return `Ontem ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      return `${days[date.getDay()]} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  } catch {
    return '';
  }
}

/**
 * Envia mensagem para admin
 */
export async function sendMessageToAdmin(
  nutritionistId: string,
  text: string,
  imageUrl?: string,
  audioUrl?: string
): Promise<void> {
  if (!nutritionistId || (!text.trim() && !imageUrl && !audioUrl)) return;

  try {
    // Criar/atualizar metadados se necessário
    const metadata = await getAdminChatConversation(nutritionistId);
    if (!metadata) {
      throw new Error('Não foi possível criar conversa com admin');
    }

    // Enviar mensagem
    const messagesRef = ref(database, `adminChat/${nutritionistId}/messages`);
    const newMsgRef = push(messagesRef);
    const messageData: any = {
      author: 'nutritionist',
      text: text || '',
      createdAt: serverTimestamp(),
    };

    if (imageUrl) {
      messageData.image = imageUrl;
    }

    if (audioUrl) {
      messageData.audio = audioUrl;
    }

    await set(newMsgRef, messageData);

    // Atualizar metadados
    const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);
    const lastMessage = text || (imageUrl ? '📷 Imagem' : '🎤 Áudio');
    await update(metadataRef, {
      lastMessage,
      lastMessageTime: Date.now(),
      unreadCount: (metadata.unreadCount || 0) + 1, // Incrementa contador não lido do admin
    });
  } catch (error) {
    console.error('Erro ao enviar mensagem para admin:', error);
    throw error;
  }
}

/**
 * Marca mensagens como lidas
 */
export async function markAsRead(nutritionistId: string, userId: string): Promise<void> {
  if (!nutritionistId || !userId) return;

  try {
    const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);
    const metadataSnap = await get(metadataRef);

    if (!metadataSnap.exists()) return;

    // Determinar qual contador zerar baseado no usuário
    // Se userId é o nutritionistId, então é o nutricionista marcando como lida
    // Caso contrário, é o admin
    if (userId === nutritionistId) {
      await update(metadataRef, {
        unreadCountNutritionist: 0,
      });
    } else {
      await update(metadataRef, {
        unreadCount: 0,
      });
    }
  } catch (error) {
    console.error('Erro ao marcar mensagens como lidas:', error);
  }
}

/**
 * Listener em tempo real para mensagens
 */
export function subscribeToAdminChatMessages(
  nutritionistId: string,
  callback: (messages: AdminChatMessage[]) => void
): () => void {
  if (!nutritionistId) {
    return () => {};
  }

  const messagesRef = ref(database, `adminChat/${nutritionistId}/messages`);
  const messagesQuery = query(messagesRef, orderByChild('createdAt'));

  const unsubscribe = onValue(messagesQuery, async () => {
    const messages = await getAdminChatMessages(nutritionistId);
    callback(messages);
  }, (error) => {
    console.error('Erro no listener de mensagens:', error);
    callback([]);
  });

  return unsubscribe;
}

/**
 * Listener em tempo real para metadados (contador de não lidas)
 */
export function subscribeToAdminChatMetadata(
  nutritionistId: string,
  callback: (metadata: AdminChatMetadata | null) => void
): () => void {
  if (!nutritionistId) {
    return () => {};
  }

  const metadataRef = ref(database, `adminChat/${nutritionistId}/metadata`);

  const unsubscribe = onValue(metadataRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.val() as AdminChatMetadata);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Erro no listener de metadados:', error);
    callback(null);
  });

  return unsubscribe;
}




